import { AxiosResponse } from 'axios'
import { apiNode } from './api'

const URL = '2/users/v2'

export interface MfaStatusResponse {
	success: boolean
	data: {
		value: boolean
	}
}

export interface MfaLoginRequest {
	username: string
	password: string
}

export interface MfaLoginResponse {
	success: boolean
	data?: {
		access_token: string
		refresh_token?: string
		token_type?: string
		expires_in?: number
	}
	error?: 'mfa_required'
	mfa_token?: string
	details?: unknown
}

export interface MfaChallengeRequest {
	mfa_token: string
	authenticator_id?: string
	challenge_type?: 'oob' | 'otp'
}

export interface MfaVerifyRequest {
	mfa_token: string
	otp: string
}

export interface MfaVerifyResponse {
	success: boolean
	data?: {
		access_token: string
		refresh_token?: string
		token_type?: string
		expires_in?: number
	}
}

export interface MfaToggleRequest {
	username?: string
	password?: string
	mfa_token?: string
	otp?: string
	value: boolean
}

export type MfaToggleStep =
	| 'otp_required'
	| 'password_required'
	| 'password_invalid'
	| 'otp_invalid'
	| 'auth_error'
	| 'no_access_token'

export interface MfaToggleResponse {
	success: boolean
	data?: {
		value?: boolean
		redirectUrl?: string
		raw?: unknown
	}
	step?: MfaToggleStep
	mfa_token?: string
	message?: string
	details?: unknown
}

export const MfaService = {
	/**
	 * Get current MFA status for the user
	 */
	async getStatus(): Promise<MfaStatusResponse> {
		const response: AxiosResponse<MfaStatusResponse> = await apiNode.get(
			`${URL}/user/mfa`,
		)
		return response.data
	},

	/**
	 * Login with username/password (may return mfa_required)
	 */
	async login(data: MfaLoginRequest): Promise<MfaLoginResponse> {
		const response: AxiosResponse<MfaLoginResponse> = await apiNode.post(
			`${URL}/user/token`,
			data,
		)
		return response.data
	},

	/**
	 * Trigger MFA challenge (for push/sms/email)
	 */
	async challenge(
		data: MfaChallengeRequest,
	): Promise<{ success: boolean; data: unknown }> {
		const response = await apiNode.post(`${URL}/user/mfa/challenge`, data)
		return response.data
	},

	/**
	 * Verify OTP code
	 */
	async verifyOtp(data: MfaVerifyRequest): Promise<MfaVerifyResponse> {
		const response: AxiosResponse<MfaVerifyResponse> = await apiNode.post(
			`${URL}/user/mfa/verify`,
			data,
		)
		return response.data
	},

	/**
	 * Toggle MFA on/off
	 * Can be called with username/password OR with mfa_token/otp
	 */
	async toggle(data: MfaToggleRequest): Promise<MfaToggleResponse> {
		const response: AxiosResponse<MfaToggleResponse> = await apiNode.post(
			`${URL}/user/mfa/toggle`,
			data,
		)
		return response.data
	},

	/**
	 * Skip MFA setup (post-registration)
	 */
	async skip(): Promise<{ success: boolean; data: unknown }> {
		const response = await apiNode.post(`${URL}/user/mfa/skip`, {})
		return response.data
	},
}
